import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { SecretRevealDialog } from './SecretRevealDialog';

import { QueryError } from '@/components/QueryError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';

// Réponse de GET /integrations/api-keys (tenants-service) : tableau simple
export interface ApiKey {
  id: string; name: string; keyPrefix: string; status: string; scopes: string[];
  lastUsedAt: string | null; expiresAt: string | null; createdAt: string;
}

// Aligné sur VALID_SCOPES (tenants-service, routes/integrations)
const SCOPES: { value: string; label: string }[] = [
  { value: 'vehicles:read', label: 'Véhicules' },
  { value: 'missions:read', label: 'Missions' },
  { value: 'alerts:read', label: 'Alertes' },
  { value: 'positions:read', label: 'Positions GPS' },
  { value: 'fuel:read', label: 'Carburant' },
  { value: 'maintenance:read', label: 'Maintenance' },
  { value: '*', label: 'Tout (lecture complète)' },
];

const EMPTY_FORM = { name: '', scopes: [] as string[], expiresAt: '' };

/** Onglet « Clés API » : liste sous integrations.read, création et révocation sous integrations.write. */
export function ApiKeysTab() {
  const queryClient = useQueryClient();
  const canWrite = useHasPermission('integrations.write');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  const keysQuery = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => (await api.get<ApiKey[]>('/integrations/api-keys')).data,
  });
  const { data: apiKeys } = keysQuery;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['api-keys'] });

  const create = useMutation({
    mutationFn: async () => {
      // Date seule saisie → fin de journée UTC (le schéma attend un datetime ISO)
      const body = {
        name: form.name.trim(),
        scopes: form.scopes,
        ...(form.expiresAt ? { expiresAt: new Date(`${form.expiresAt}T23:59:59Z`).toISOString() } : {}),
      };
      return (await api.post<ApiKey & { key: string }>('/integrations/api-keys', body)).data;
    },
    onSuccess: async (created) => {
      setCreating(false);
      setForm(EMPTY_FORM);
      setRevealed(created.key);
      await refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/integrations/api-keys/${id}`); },
    onSuccess: async () => {
      toast.success('Clé révoquée');
      setRevoking(null);
      await refresh();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Révocation impossible')),
  });

  const toggleScope = (scope: string) =>
    setForm((f) => ({ ...f, scopes: f.scopes.includes(scope) ? f.scopes.filter((s) => s !== scope) : [...f.scopes, scope] }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Clés API</CardTitle>
        {canWrite && (
          <Button onClick={() => { create.reset(); setCreating(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Créer une clé
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {apiKeys?.map((k) => (
            <div key={k.id} className="flex items-center justify-between gap-4 p-3 border rounded">
              <div className="min-w-0">
                <p className="font-medium text-sm">{k.name}</p>
                <p className="text-xs text-muted-foreground font-mono">{k.keyPrefix}•••••••• · {k.scopes.join(', ')}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <p className="text-xs text-muted-foreground">
                  {k.lastUsedAt ? `Utilisée ${new Date(k.lastUsedAt).toLocaleDateString('fr-FR')}` : 'Jamais utilisée'}
                </p>
                {k.status === 'ACTIVE' ? (
                  canWrite && (
                    <Button size="sm" variant="outline" onClick={() => setRevoking(k)} aria-label={`Révoquer ${k.name}`}>
                      Révoquer
                    </Button>
                  )
                ) : (
                  <Badge variant="secondary">{k.status === 'REVOKED' ? 'Révoquée' : k.status}</Badge>
                )}
              </div>
            </div>
          ))}
          <QueryError query={keysQuery} />
          {apiKeys?.length === 0 && <p className="text-muted-foreground text-sm">Aucune clé API</p>}
        </div>
      </CardContent>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle clé API</DialogTitle>
            <DialogDescription>Accès en lecture à l’API publique, limité aux périmètres cochés.</DialogDescription>
          </DialogHeader>
          <form id="api-key-form" className="space-y-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <div className="space-y-1">
              <Label htmlFor="api-key-name">Nom</Label>
              <Input id="api-key-name" required minLength={2} maxLength={80} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <fieldset className="space-y-1">
              <legend className="text-sm font-medium">Périmètres</legend>
              {SCOPES.map((s) => (
                <label key={s.value} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.scopes.includes(s.value)} onChange={() => toggleScope(s.value)} />
                  {s.label} <span className="font-mono text-xs text-muted-foreground">{s.value}</span>
                </label>
              ))}
            </fieldset>
            <div className="space-y-1">
              <Label htmlFor="api-key-expires">Expiration (facultatif)</Label>
              <Input id="api-key-expires" type="date" value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
            </div>
            {create.isError && (
              <p role="alert" className="text-sm text-danger">{apiErrorMessage(create.error, 'Création impossible')}</p>
            )}
          </form>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setCreating(false)}>Annuler</Button>
            <Button type="submit" form="api-key-form" disabled={create.isPending || form.scopes.length === 0}>
              {create.isPending ? 'Création…' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revoking} onOpenChange={(open) => { if (!open) setRevoking(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Révoquer la clé</DialogTitle>
            <DialogDescription>
              {revoking?.name} ({revoking?.keyPrefix}…) cessera immédiatement de fonctionner. Action irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setRevoking(null)}>Annuler</Button>
            <Button variant="destructive" disabled={revoke.isPending} onClick={() => revoking && revoke.mutate(revoking.id)}>
              {revoke.isPending ? 'Révocation…' : 'Révoquer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SecretRevealDialog
        secret={revealed}
        title="Clé API créée"
        description="Transmettez-la au système partenaire dans l’en-tête X-Api-Key."
        onClose={() => setRevealed(null)}
      />
    </Card>
  );
}
